<script setup lang="ts">
import ChildCardsList from '../../components/ChildCradsList/ChildCradsList.vue';
import ChildAddEditCard from '../../components/ChildAddEditCard/ChildAddEditCard.vue';
import ChildTokenCard from '../../components/ChildTokenCard/ChildTokenCard.vue';
import SettingsCheckoutForm from 'src/modules/Setting/components/SettingsCheckoutForm/SettingsCheckoutForm.vue';
import {
  mdiPlus,
  mdiClose,
  mdiChevronDoubleLeft,
  mdiContentCopy,
  mdiExclamation,
} from '@quasar/extras/mdi-v7';
import type { LiqpayCheckoutResponse } from 'src/types/strapi/customTypes';
import { computed, inject, ref, onMounted } from 'vue';
import type { ComputedRef } from 'vue';
import { useManageChild } from './useManageChild';
import type { Child } from '../../models/Child';
import { usePlan } from 'src/modules/Setting/views/SettingBillingView/usePlan';
import type { Plan } from 'src/modules/Setting/views/SettingBillingView/usePlan';
import { useShare } from '@vueuse/core';
import { t } from 'boot/i18n';
import { useRoute } from 'vue-router';
import { Notify } from 'quasar';
import { useClipboard } from 'shared/composables/useClipboard/useClipboard';

const currentUserId = inject('currentUserId') as ComputedRef<number>;

const {
  currentChild,
  isEditing,
  dialogContent,
  showDialog,
  children,
  childLoginToken,
  getCheckoutData,
  editChild,
  saveNewChild,
  updateChild,
  deleteChild,
  loginToken,
  newChild,
  toggleChildActive,
  getChildLicenseData,
} = useManageChild(currentUserId.value);

const { share, isSupported: isSupportedShare } = useShare();
function shareCode($event: string) {
  void (
    isSupportedShare.value &&
    share({
      title: t('CHILED_LOGIN_TOKEN'),
      text: $event,
    })
  );
}

const { copy } = useClipboard();
const copied = ref(false);

async function copyCode(text: string) {
  const success = await copy(text);

  if (success) {
    copied.value = true;

    Notify.create({
      message: t('COPIED_TO_CLIPBOARD'),
      color: 'positive',
      icon: mdiContentCopy,
      position: 'bottom',
      timeout: 2000,
    });

    setTimeout(() => {
      copied.value = false;
    }, 2000);
  } else {
    Notify.create({
      message: t('FAILED_TO_COPY'),
      color: 'negative',
      icon: mdiExclamation,
      position: 'bottom',
      timeout: 3000,
    });
  }
}

// New state for buy license selection dialog
const showBuyLicenseDialog = ref(false);
const showLicenseDelayDialog = ref(false);
const selectedChildForLicense = ref<Child | null>(null);

const buyLicenseDialoge = (child: Child) => {
  selectedChildForLicense.value = child;
  selectedOption.value = null;
  planData.value = null;
  showBuyLicenseDialog.value = true;
};

onMounted(() => {
  const route = useRoute();
  const {
    query: { 'child-id': queryChildId },
  } = route;

  console.log('Query Child ID:', queryChildId);

  if (queryChildId) {
    // Remove the query parameter from the URL after showing the dialog
    history.replaceState(null, '', window.location.pathname);
    showLicenseDelayDialog.value = true;
  }
});
// Mock prices (replace with real plan pricing if available)
const { plansOptions, plans } = usePlan();

const selectedOption = ref<{ label: string; value: number } | null>(null);

const selectedPlan = computed(() => {
  const plan = plans.value?.data.find(
    (p: Plan) => p.id === selectedOption.value?.value,
  );
  return plan || { name: t('PLAN_ERROR'), price: 0, currency: '', period: '' };
});
const planData = ref<LiqpayCheckoutResponse | null>(null);

async function purchaseLicense(period: 'month' | 'year') {
  if (!selectedChildForLicense.value) return;

  planData.value = await getCheckoutData(
    period,
    selectedChildForLicense.value?.id,
  );
  if (planData.value) {
    console.log('License purchase data:', planData.value);
  }
  if (
    !planData.value?.liqPayPayment &&
    planData.value?.action === 'subscribe' &&
    planData.value?.result === 'ok'
  ) {
    showBuyLicenseDialog.value = false;
    showLicenseDelayDialog.value = true;
    intervalCounter.value = 0;
    await getChildLicenseInterval(selectedChildForLicense.value.id);
  }
}

// Interval to check for license activation
const intervalCounter = ref(0);
const processingLicense = ref(false);
async function getChildLicenseInterval(childId: number) {
  if (intervalCounter.value > 12) {
    console.log('Stopping license check after 12 attempts');
    processingLicense.value = false;
    return;
  }
  processingLicense.value = true;
  intervalCounter.value += 1;
  const childRes = await getChildLicenseData(childId);
  if (!childRes?.[0]?.validLicense) {
    setTimeout(() => {
      void getChildLicenseInterval(childId);
    }, 3000); // 3 seconds
  } else {
    processingLicense.value = false;
  }
}

const handleCheckOut = () => {
  if (!selectedChildForLicense.value) return;

  // Not implemented, done in backend.
};
</script>

<template>
  <div v-if="children && children.length > 0">
    <ChildCardsList
      :children="children as Child[]"
      class="rounded-borders q-pa-none"
      :processing-license="processingLicense"
      :selected-child-for-license="selectedChildForLicense as Child"
      @edit-child="editChild"
      @delete-child="deleteChild"
      @toggle-active="toggleChildActive"
      @checkout="buyLicenseDialoge"
      @login-token="loginToken"
    />
  </div>
  <div v-else>
    <div class="text-h6">No children found</div>
    <div class="text-subtitle2">Please add a child to get started.</div>
  </div>
  <q-page-sticky
    position="bottom-right"
    :offset="[24, 24]"
  >
    <q-btn
      fab
      :icon="mdiPlus"
      color="primary"
      @click="newChild"
    />
  </q-page-sticky>
  <q-dialog
    v-if="currentChild"
    v-model="showDialog"
  >
    <ChildAddEditCard
      v-if="dialogContent === 'addEditChild'"
      v-model:child="currentChild"
      :is-editing="isEditing"
      class="bg-primary text-dark dialog-surface"
      style="min-width: 350px"
      @save-child="isEditing ? updateChild() : saveNewChild()"
    />
    <ChildTokenCard
      v-else-if="dialogContent === 'loginToken'"
      :child-login-token="childLoginToken"
      class="bg-primary text-dark dialog-surface"
      style="min-width: 350px"
      :copied="copied"
      @copy="copyCode"
      @share="shareCode"
    />
  </q-dialog>

  <q-dialog
    v-model="showLicenseDelayDialog"
    dark
  >
    <q-card>
      <q-card-section class="row items-center q-pb-none">
        <div class="text-h6">{{ $t('PAYMENT_CONFIRMATION_TITLE') }}</div>
        <q-space />
        <q-btn
          v-close-popup
          :icon="mdiClose"
          flat
          round
          dense
        />
      </q-card-section>

      <q-card-section>
        <div class="text-subtitle2">{{ $t('PAYMENT_CONFIRMATION_TEXT') }}</div>
      </q-card-section>
    </q-card>
  </q-dialog>

  <!-- Buy License Selection Dialog -->
  <q-dialog
    v-model="showBuyLicenseDialog"
    persistent
  >
    <q-card
      style="min-width: 320px"
      class="bg-dark text-white"
    >
      <q-card-section>
        <q-toolbar class="q-px-none">
          <q-toolbar-title>{{ $t('BUY_LICENSE') }}</q-toolbar-title>
          <q-btn
            v-close-popup
            flat
            round
            dense
            :icon="mdiClose"
            @click="
              planData = null;
              selectedOption = null;
            "
          />
        </q-toolbar>
        <div class="text-caption q-mt-xs">
          {{
            selectedChildForLicense
              ? `${$t('FOR_CHILD')}: ${selectedChildForLicense.firstname} ${selectedChildForLicense.lastname}`
              : ''
          }}
        </div>
      </q-card-section>

      <q-separator dark />

      <q-card-section
        v-if="!planData"
        class="q-pt-sm"
      >
        <div class="column q-gutter-sm">
          <q-radio
            v-for="plan in plansOptions"
            :key="plan.value"
            v-model="selectedOption"
            :val="plan"
            :label="plan.label"
            dark
          />
        </div>
      </q-card-section>

      <q-separator
        v-if="planData"
        dark
      />

      <q-card-section
        v-if="!planData"
        class="row justify-between q-gutter-sm"
      >
        <q-btn
          unelevated
          color="primary"
          :disable="!selectedPlan || !selectedPlan?.period"
          class="justify-between"
          @click="purchaseLicense(selectedPlan?.period)"
        >
          {{ selectedPlan?.name ?? $t('SELECTED_PLAN') }}
        </q-btn>
      </q-card-section>

      <q-card-section v-if="planData">
        <q-btn
          flat
          color="grey-5"
          :icon="mdiChevronDoubleLeft"
          @click="
            planData = null;
            selectedOption = null;
          "
        >
          {{ $t('BACK') }}
        </q-btn>
      </q-card-section>

      <q-card-section
        v-if="planData && selectedOption"
        class="text-center text-bold text-h6"
      >
        {{ selectedOption.label }}
      </q-card-section>

      <q-card-section
        v-if="planData && selectedOption"
        class="text-center text-caption"
      >
        {{ $t('NO_VALID_PAYMENT_DATA_FOUND_CHECKOUT_TO_PAY') }}
      </q-card-section>

      <q-card-section
        v-if="planData"
        class="q-pb-xl"
      >
        <SettingsCheckoutForm
          :plan-data="planData"
          class="column items-center"
          @checkout="handleCheckOut"
        />
      </q-card-section>
    </q-card>
  </q-dialog>
</template>
